/**
 * @fileoverview Eurofarma Regulatorio Dashboard page component
 * Displays statistics and quick access buttons for Eurofarma regulatory compliance users
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchData, intl, HTTP_STATUS, havePermissions } from "dmsbasereact";

import { DashboardLayout } from "../../components/Dashboard";
import { APP_ROUTES } from "../../utils/NamedRoutes";
import { APP_COLORS } from "../../theme/appTheme";
import { Permissions } from "../../constants/Permissions";

// MUI Icons
import DescriptionIcon from "@mui/icons-material/Description";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import BusinessIcon from "@mui/icons-material/Business";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

/**
 * EurofarmaRegulatoriooDashboard - Dashboard page for Eurofarma regulatory compliance users
 * @returns {JSX.Element} The Eurofarma Regulatorio dashboard with stats and quick access
 */
export const EurofarmaRegulatoriooDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);

    /**
     * Fetch dashboard stats from API
     */
    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const statsResponse = await fetchData("/dashboard/eurofarma/stats", "GET", true);

            if (statsResponse.status === HTTP_STATUS.OK_200) {
                setStats(statsResponse.data?.data);
            }
        } catch (err) {
            console.error("Error fetching eurofarma dashboard data:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    // Check if user has permission to view prescription/dispensation report
    const canViewPrescriptionDispensationReport = havePermissions(
        [Permissions.REPORTS_PRESCRIPTIONS_DISPENSATIONS, Permissions.REPORTS_EXECUTIVE],
        false // any of these permissions
    );

    // Check if user has permission to view institutions report
    const canViewInstitutionsReport = havePermissions(
        [Permissions.REPORTS_INSTITUTIONS, Permissions.REPORTS_DETAILED],
        false // any of these permissions
    );

    // Stats configuration
    const statsConfig = [
        {
            title: intl.get("TotalPrescriptions").d("Total Prescriptions"),
            value: stats?.total_prescriptions || 0,
            icon: DescriptionIcon,
            color: APP_COLORS.primary.main,
            onClick: canViewPrescriptionDispensationReport
                ? () => navigate(APP_ROUTES.ReportsPrescriptionDispensation)
                : undefined,
        },
        {
            title: intl.get("TotalDispensations").d("Total Dispensations"),
            value: stats?.total_dispensations || 0,
            icon: VaccinesIcon,
            color: APP_COLORS.system.success,
            onClick: canViewPrescriptionDispensationReport
                ? () => navigate(APP_ROUTES.ReportsPrescriptionDispensation)
                : undefined,
        },
        {
            title: intl.get("TotalReturns").d("Total Returns"),
            value: stats?.pending_returns || 0,
            icon: AssignmentReturnIcon,
            color: APP_COLORS.system.warning,
        },
        {
            title: intl.get("TotalInstitutions").d("Total Institutions"),
            value: stats?.total_institutions || 0,
            icon: BusinessIcon,
            color: APP_COLORS.system.info,
            onClick: canViewInstitutionsReport
                ? () => navigate(APP_ROUTES.ReportsInstitutions)
                : undefined,
        },
    ];

    // Quick access buttons configuration
    const quickAccessConfig = [
        {
            label: intl.get("Analytics.Menu.Traceability").d("Traceability"),
            icon: TimelineIcon,
            route: APP_ROUTES.AnalyticsTraceability,
            color: APP_COLORS.system.success,
        },
        {
            label: intl.get("Analytics.Menu.Sales").d("Sales"),
            icon: TrendingUpIcon,
            route: APP_ROUTES.AnalyticsSales,
            color: APP_COLORS.system.info,
        },
    ];

    return (
        <DashboardLayout
            welcomeName={stats?.user_name}
            loading={loading}
            stats={statsConfig}
            quickAccess={quickAccessConfig}
            alerts={[]}
        />
    );
};
